import type { StatusTone } from '@shared/design-system/ui'
import type { DebtOrigin } from '@shared/contracts/enums'
import { Money } from '@shared/lib/money/money'
import type { MonetaryValue } from '@shared/lib/money/money'
import type { BalanceSummary, OpenDebt } from '../api/wallet.contract'

type DebtPresentationInput =
  | DebtOrigin
  | Pick<OpenDebt, 'origin' | 'handledByGlobalChargeback' | 'saleStatus'>

function debtOriginOf(input: DebtPresentationInput): DebtOrigin {
  return typeof input === 'string' ? input : input.origin
}

function isGlobalChargebackRefund(input: DebtPresentationInput): boolean {
  return (
    typeof input !== 'string' &&
    input.origin === 'ESTORNO' &&
    Boolean(input.handledByGlobalChargeback)
  )
}

export function debtOriginLabel(input: DebtPresentationInput): string {
  if (isGlobalChargebackRefund(input)) {
    return 'Reembolso de chargeback'
  }

  const labels: Record<DebtOrigin, string> = {
    CHARGEBACK: 'Chargeback',
    ESTORNO: 'Estorno',
    GLOBAL_CHARGEBACK_FEE: 'Multa de chargeback',
    FX_LOSS: 'Ajuste por perda cambial',
  }
  return labels[debtOriginOf(input)]
}

export function debtOriginTone(input: DebtPresentationInput): StatusTone {
  const origin = debtOriginOf(input)
  if (origin === 'GLOBAL_CHARGEBACK_FEE') {
    return 'informative'
  }
  return origin === 'ESTORNO' && !isGlobalChargebackRefund(input) ? 'informative' : 'closed'
}

export function debtOriginDescription(input: DebtPresentationInput): string {
  if (isGlobalChargebackRefund(input)) {
    return 'O chargeback foi prevenido por alerta e a venda foi reembolsada antes da disputa. O valor é recuperado das suas próximas entradas.'
  }

  const labels: Record<DebtOrigin, string> = {
    CHARGEBACK:
      'O comprador contestou a cobrança no cartão. O valor foi devolvido a ele e é recuperado das suas próximas entradas.',
    ESTORNO:
      'Esta venda foi estornada. O valor devolvido ao comprador é recuperado das suas próximas entradas.',
    GLOBAL_CHARGEBACK_FEE: 'Multa fixa Vendepay cobrada quando a venda vira chargeback.',
    FX_LOSS: 'Perda cambial efetiva observada na Stripe, separada da multa.',
  }
  return labels[debtOriginOf(input)]
}

export function debtStatusLabel(debt: Pick<OpenDebt, 'status'>): string {
  return debt.status === 'SETTLED' ? 'Quitada' : 'Pendente'
}

export function debtRecoveredAmount(
  debt: Pick<OpenDebt, 'originalAmount' | 'currentAmount'>,
): MonetaryValue {
  const recovered = Money.fromMonetaryValue(debt.originalAmount).subtract(
    Money.fromMonetaryValue(debt.currentAmount),
  )
  if (recovered.isNegative()) {
    return new Money('0', debt.originalAmount.currency).toMonetaryValue()
  }
  return recovered.toMonetaryValue()
}

export function debtRecoveryRatio(
  debt: Pick<OpenDebt, 'originalAmount' | 'currentAmount'>,
): number {
  const original = Money.fromMonetaryValue(debt.originalAmount)
  if (original.isZero()) {
    return 0
  }
  const recovered = Money.fromMonetaryValue(debtRecoveredAmount(debt)).toNumber()
  const ratio = recovered / original.toNumber()
  if (ratio < 0) {
    return 0
  }
  if (ratio > 1) {
    return 1
  }
  return ratio
}

export function isOpenDebt(debt: OpenDebt): boolean {
  return debt.status === 'PENDING' && !Money.fromMonetaryValue(debt.currentAmount).isZero()
}

export function openDebts(debts: OpenDebt[]): OpenDebt[] {
  return debts.filter(isOpenDebt)
}

export function hasOpenDebt(balance: Pick<BalanceSummary, 'openDebt'>): boolean {
  const value = Money.fromMonetaryValue(balance.openDebt)
  return !value.isZero() && !value.isNegative()
}

export function totalOpenDebtByCurrency(debts: OpenDebt[]): MonetaryValue[] {
  const byCurrency = new Map<string, Money>()
  for (const debt of openDebts(debts)) {
    const current = byCurrency.get(debt.currentAmount.currency)
    const value = Money.fromMonetaryValue(debt.currentAmount)
    byCurrency.set(debt.currentAmount.currency, current ? current.add(value) : value)
  }
  return [...byCurrency.values()].map((total) => total.toMonetaryValue())
}
